import { zodResolver } from '@hookform/resolvers/zod';
import { useId, useRef } from 'react';
import type { FieldValues, UseFormProps, UseFormReturn } from 'react-hook-form';
import { useForm } from 'react-hook-form';
import type { z } from 'zod';

export type UseZodFormReturn<TFormValues extends FieldValues> =
  UseFormReturn<TFormValues> & {
    custom: {
      /**
       * A unique id for the form - when placing the submit button outside of the <form>`-tag
       */
      uniqueId: string;
      /**
       * A ref to the `<form>`-element
       */
      formRef: React.RefObject<HTMLFormElement>;
    };
  };
/**
 * Helper function to create a `react-hook-form` with a Zod schema.
 * Automatically infers the shape of the Zod schema and adds a validator.
 */
export function useZodForm<TSchema extends z.ZodType>(
  props: Omit<UseFormProps<TSchema['_input']>, 'resolver'> & {
    schema: TSchema;
  } & (Extract<TSchema['_input'], FormData> extends never
      ? {
          isFormData?: never;
        }
      : {
          /**
           * You are passing `FormData`, so you need to instruct that it is formData
           */
          isFormData: true;
        }),
): UseZodFormReturn<TSchema['_input']> {
  const formRef = useRef<HTMLFormElement>(null);

  const _resolver = zodResolver(props.schema, undefined, {
    raw: true,
  });
  const form = useForm<TSchema['_input']>({
    ...props,
    resolver: props.isFormData
      ? (_, ctx, opts) => {
          if (!formRef.current) {
            return {
              values: {},
              errors: {
                root: {
                  message: 'Form not mounted',
                },
              },
            };
          }
          const values = new FormData(formRef.current);
          return _resolver(values, ctx, opts);
        }
      : _resolver,
  }) as UseZodFormReturn<TSchema['_input']>;

  form.custom = {
    uniqueId: useId(),
    formRef,
  };

  return form;
}

// biome-ignore lint/suspicious/noExplicitAny: ok
export type AnyUseZodFormReturn = UseZodFormReturn<any>;
